import React, { useState, useEffect, useCallback } from 'react';

// Types
import { OnboardingWizardStep } from '../../OnboardingWizard/types';

// Hooks
import { useOnboardingState } from 'Shared/utils/hooks/use-onboarding-state';
import { useToggle } from 'Shared/utils/hooks';

// Actions
import {
  fetchAgendaAiSlackChannels,
  generateAgendaAiSuggestionsPoll,
  generateAgendaAiSuggestions,
} from 'Shared/actions/onboardingState';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton } from 'Wpds/components/button';
import { WpGif } from 'Wpds/components/gif';
import OnboardingSlackTitle from './OnboardingSlackTitle';
import SlackWorkspaceSelect from 'Shared/components/SlackWorkspaceSelect/SlackWorkspaceSelect';

// Styles
import './OnboardingSlackAIGenerate.scss';

const OnboardingSlackAIGenerate = () => {
  const {
    step,
    onboardingState,
    handleAdvanceOnboardingState,
    handleAdvanceBackOnboardingState,
  } = useOnboardingState();

  const [availableChannels, setAvailableChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [isFetchingChannels, toggleIsFetchingChannels] = useToggle(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isGenerating, toggleIsGenerating] = useToggle(false);
  const [isReadyForGenerationPoll, setIsReadyForGenerationPoll] = useState<boolean>(false);

  const [discussionTopics, setDiscussionTopics] = useState<string[]>([]);
  const [actionItems, setActionItems] = useState<string[]>([]);

  const workspaceName = onboardingState?.workspaces[0]?.name;
  const hasGeneratedAgenda = discussionTopics.length > 0 || actionItems.length > 0;

  const handleSetSelectedChannels = channels => {
    setHasError(false);
    setSelectedChannels(channels);
  };

  const handleGenerateAgendaClick = () => {
    if (selectedChannels.length === 0) {
      setHasError(true);
      return;
    }

    setDiscussionTopics([]);
    setActionItems([]);

    toggleIsGenerating();

    generateAgendaAiSuggestions({
      channelIds: selectedChannels.map(channel => channel.id),
    }).then(() => {
      setIsReadyForGenerationPoll(true);
    });
  };

  useEffect(() => {
    if (!isReadyForGenerationPoll) return;
    const generationPollInterval = setInterval(() => {
      generateAgendaAiSuggestionsPoll({ channelIds: selectedChannels.map(channel => channel.id) }).then(response => {
        if (response.status === 200 && Object.keys(response.data).length) {
          setDiscussionTopics(response.data.discussion_topics);
          setActionItems(response.data.action_items);
          setIsReadyForGenerationPoll(false);
          toggleIsGenerating();
        }
        if (response.status !== 200) {
          setIsReadyForGenerationPoll(false);
          toggleIsGenerating();
        }
      });
    }, 5000);

    return () => clearInterval(generationPollInterval);
  }, [isReadyForGenerationPoll]);

  const handleNextClick = () => {
    handleAdvanceOnboardingState({
      onboardingState: {
        agenda_from_chat: {
          channels: selectedChannels.map(({ id }) => id),
          discussion_topics: discussionTopics,
          action_items: actionItems,
        },
      },
    });
  };

  const handleRemoveDiscussionTopic = useCallback(
    item => {
      setDiscussionTopics(discussionTopics.filter(topic => topic !== item));
    },
    [discussionTopics, setDiscussionTopics]
  );

  const handleRemoveActionItem = useCallback(
    item => {
      setActionItems(actionItems.filter(ai => ai !== item));
    },
    [actionItems, setActionItems]
  );

  useEffect(() => {
    if (availableChannels.length > 0) return;
    if (step !== OnboardingWizardStep.generateAgenda) return;
    fetchAgendaAiSlackChannels()
      .then(response => {
        if (response.status === 200) {
          setAvailableChannels(response.data);
        }
      })
      .finally(() => toggleIsFetchingChannels());
  }, [step, availableChannels]);

  const baseClassName = 'OnboardingSlackAIGenerate';

  return (
    <div className={baseClassName}>
      <OnboardingSlackTitle
        onBack={handleAdvanceBackOnboardingState}
        onNext={hasGeneratedAgenda ? handleNextClick : null}
        title="Generate Agenda"
      />
      <div>
        <p className={`${baseClassName}__desription`}>Which Slack channels are relevant to {workspaceName}?</p>
        <div className={`${baseClassName}__channel-input`}>
          <SlackWorkspaceSelect
            options={availableChannels}
            selected={selectedChannels}
            onChange={handleSetSelectedChannels}
            errorMsg={hasError ? 'Please select at least one channel' : null}
            isDisabled={isGenerating || isFetchingChannels}
            showLabel={false}
          />
        </div>
        <div className={`${baseClassName}__button`}>
          <WpButton
            block
            onClick={handleGenerateAgendaClick}
            loading={isGenerating}
            variant={hasGeneratedAgenda ? 'secondary' : 'primary'}
            size="lg"
          >
            {hasGeneratedAgenda ? 'Regenerate' : 'Generate'} Agenda
          </WpButton>
        </div>
      </div>

      {isGenerating && (
        <div className={`${baseClassName}__fetching`}>
          <div className={`${baseClassName}__fetching__block`}>
            <p>Scanning Slack Channels...</p>
            <WpGif url="https://media4.giphy.com/media/dKOlmnaAMtzJwEE9V8/200w.gif?cid=bb696112hdyyjjx5e2t2yirb3es58nrbfgfbb8to9mkubako&rid=200w.gif&ct=g" />
          </div>
          <div className={`${baseClassName}__fetching__block`}>
            <p>Pulling out relevant info...</p>
            <WpGif url="https://media3.giphy.com/media/5TC17m6sd4RhgTjyg6/200w.gif?cid=6c09b9527btvo3wq5ka05s6em0s6w5orvnba4cvxtmeco592&rid=200w.gif&ct=g" />
          </div>
          <div className={`${baseClassName}__fetching__block`}>
            <p>Generating Topics...</p>
            <WpGif url="https://media4.giphy.com/media/H1dxi6xdh4NGQCZSvz/200w.gif?cid=bb696112a98ybq1glxqrn5a4h7a4k2helv572n42nrazt2tk&rid=200w.gif&ct=g" />
          </div>
        </div>
      )}
      {discussionTopics.length > 0 && (
        <div className={`${baseClassName}__print-out-section`}>
          <p className={`${baseClassName}__print-out-section__title`}>Discussion Topics</p>
          <ul className={`${baseClassName}__print-out-section__list`}>
            {discussionTopics.map((item, index) => (
              <li key={`discussion-topic-${index}`}>
                <div className={`${baseClassName}__print-out-section__list__item`}>
                  <span className={`${baseClassName}__print-out-section__list__item__desc`}>{item}</span>
                  <div>
                    <WpButton size="sm" variant="tertiary" onClick={() => handleRemoveDiscussionTopic(item)}>
                      <WpIcon name="times" />
                    </WpButton>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {actionItems.length > 0 && (
        <div className={`${baseClassName}__print-out-section`}>
          <p className={`${baseClassName}__print-out-section__title`}>Action Items</p>
          <ul className={`${baseClassName}__print-out-section__list`}>
            {actionItems.map((item, index) => (
              <li key={`action-item-${index}`}>
                <div className={`${baseClassName}__print-out-section__list__item`}>
                  <span className={`${baseClassName}__print-out-section__list__item__desc`}>{item}</span>
                  <div>
                    <WpButton size="sm" variant="tertiary" onClick={() => handleRemoveActionItem(item)}>
                      <WpIcon name="times" />
                    </WpButton>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OnboardingSlackAIGenerate;
